/* ExposureFreshness.js */

// Pure dependency-freshness evaluation for the exposure widget: compares the
// verdict document's depFreshness as-of stamps against the org freshness
// windows (ExposureManagementPrefs, authored in days) at RENDER time — so the
// chips stay honest as the page sits open, without re-running anything.
//
// Fail-closed presentation: an unknown timestamp or unreadable prefs renders as
// "unknown" and is styled like stale — the widget must never certify freshness
// it cannot prove. Windows set to 0 disable age-based staleness (matching the
// backend contract), so those dimensions read fresh whenever a timestamp exists.
const DAY_MS = 86400000;

/**
 * @param {Number|null|undefined} asOf Epoch ms the dependency was produced.
 * @param {Number|null|undefined} windowDays Org freshness window in days (0 = disabled).
 * @param {Number} now Epoch ms.
 * @returns {String} "fresh" | "stale" | "unknown"
 */
function ageStatus(asOf, windowDays, now) {
    if (typeof asOf !== "number") {
        return "unknown";
    }
    if (typeof windowDays !== "number") {
        return "unknown";
    }
    if (windowDays === 0) {
        return "fresh";
    }
    return now - asOf > windowDays * DAY_MS ? "stale" : "fresh";
}

/**
 * Short relative-age caption ("3d ago", "5h ago", "just now"); empty for unknown.
 * @param {Number|null|undefined} asOf Epoch ms.
 * @param {Number} now Epoch ms.
 * @returns {String}
 */
function formatAge(asOf, now) {
    if (typeof asOf !== "number") {
        return "";
    }
    const elapsed = Math.max(0, now - asOf);
    if (elapsed < 60000) {
        return "just now";
    }
    const minutes = Math.floor(elapsed / 60000);
    if (minutes < 60) {
        return minutes + "m ago";
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return hours + "h ago";
    }
    return Math.floor(hours / 24) + "d ago";
}

/**
 * Compact age token for tight chrome ("4m", "3h", "6d", "now"); empty for
 * unknown. The verbose sibling formatAge keeps the "ago" suffix for prose
 * captions and tooltips.
 * @param {Number|null|undefined} asOf Epoch ms.
 * @param {Number} now Epoch ms.
 * @returns {String}
 */
function formatAgeShort(asOf, now) {
    if (typeof asOf !== "number") {
        return "";
    }
    const elapsed = Math.max(0, now - asOf);
    if (elapsed < 60000) {
        return "now";
    }
    const minutes = Math.floor(elapsed / 60000);
    if (minutes < 60) {
        return minutes + "m";
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return hours + "h";
    }
    return Math.floor(hours / 24) + "d";
}

function parseHealth(depFreshness) {
    const dep = depFreshness || {};
    if (dep.parseStatus === "stale") {
        return "stale";
    }
    if (typeof dep.tagCount !== "number") {
        return "unknown";
    }
    if (dep.tagCount === 0) {
        return "broken";
    }
    return dep.parseStatus === "tags-only" ? "partial" : "ok";
}

/**
 * The per-dependency chips the widget renders.
 * @param {Object|null} depFreshness DeviceCveQualification.depFreshness
 *   ({configGeneratedAt, tagRulesVersion, p35Version, advisorySyncAt, evaluatedAt}).
 * @param {Object|null} prefs Freshness windows ({configStaleDays, advisoryStaleDays,
 *   verdictStaleDays}); null when unreadable — every age dimension reads unknown.
 * @param {Number} now Epoch ms.
 * @returns {Array<{key: String, label: String, status: String, asOf: Number|null, detail: String}>}
 */
function computeDependencyFreshness(depFreshness, prefs, now) {
    const dep = depFreshness || {};
    const windows = prefs || {};

    const configAsOf = typeof dep.configGeneratedAt === "number" ? dep.configGeneratedAt : null;
    const advisoryAsOf = typeof dep.advisorySyncAt === "number" ? dep.advisorySyncAt : null;
    const evaluatedAsOf = typeof dep.evaluatedAt === "number" ? dep.evaluatedAt : null;

    // Taxonomy is version drift, not age: the parse's tagRulesVersion vs the
    // vocabulary version the verdicts were scored with (p35Version).
    const health = parseHealth(dep);
    let taxonomyStatus = "unknown";
    let taxonomyDetail = "";
    if (health === "broken") {
        taxonomyStatus = "broken";
        taxonomyDetail = "decomposition produced no features for this device";
    } else if (health === "stale" || health === "partial") {
        taxonomyStatus = "stale";
        taxonomyDetail =
            health === "partial"
                ? "the parse is incomplete — only partial features were extracted"
                : "the stored parse is out of date";
    } else if (
        health === "ok" &&
        typeof dep.tagRulesVersion === "number" &&
        typeof dep.p35Version === "number"
    ) {
        taxonomyStatus = dep.tagRulesVersion >= dep.p35Version ? "fresh" : "stale";
        taxonomyDetail =
            taxonomyStatus === "fresh"
                ? "rules v" + dep.tagRulesVersion
                : "parsed with rules v" + dep.tagRulesVersion + ", current v" + dep.p35Version;
    } else if (typeof dep.p35Version === "number") {
        taxonomyDetail = "parse predates version stamping; current rules v" + dep.p35Version;
    }

    return [
        {
            key: "config",
            label: "Config",
            status: ageStatus(configAsOf, windows.configStaleDays, now),
            asOf: configAsOf,
            detail: formatAge(configAsOf, now),
        },
        {
            key: "taxonomy",
            label: "Taxonomy",
            status: taxonomyStatus,
            asOf: null,
            detail: taxonomyDetail,
        },
        {
            key: "advisories",
            label: "Advisories",
            status: ageStatus(advisoryAsOf, windows.advisoryStaleDays, now),
            asOf: advisoryAsOf,
            detail: formatAge(advisoryAsOf, now),
        },
        {
            key: "verdict",
            // "Qualified", not "Verdict": this row replaces the old
            // "Last Updated:" caption — it answers when the device was last
            // qualified, plus whether that is inside the org window.
            label: "Qualified",
            status: ageStatus(evaluatedAsOf, windows.verdictStaleDays, now),
            asOf: evaluatedAsOf,
            detail: formatAge(evaluatedAsOf, now),
        },
    ];
}

module.exports = {
    computeDependencyFreshness,
    parseHealth,
    ageStatus,
    formatAge,
    formatAgeShort,
    DAY_MS,
};
